import type { BlogPost } from "@/types/content";

// Joint, knee and shoulder articles (posts 16-25).
// Article images are managed centrally in src/data/blog/images.ts (keyed by slug).
export const jointPosts: BlogPost[] = [
  {
    id: 16,
    slug: "common-causes-of-knee-pain",
    title: "Common Causes of Knee Pain",
    category: "Knee Health",
    excerpt: "Knee pain has many causes across different ages. Understanding the pattern helps narrow it down.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20knee%20joint%20showing%20cartilage%20and%20ligaments%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-16&orientation=landscape&nocache=true",
    imageAlt: "Person holding the knee due to knee pain",
    metaTitle: "Common Causes of Knee Pain | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to the common causes of knee pain, from arthritis to ligament injury, and when to see an orthopaedic specialist. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-08",
    updatedDate: "2026-06-14",
    readTime: "5 min read",
    tags: ["Knee Health", "Joint Health", "Pain"],
    content: [
      {
        heading: "Why knee pain is so common",
        paragraphs: [
          "The knee is a weight-bearing joint that bends, straightens and bears load with almost every step. That makes it prone to both injury and the gradual wear of arthritis.",
        ],
      },
      {
        heading: "Common causes",
        bullets: [
          "Arthritis, including age-related osteoarthritis",
          "Ligament injuries from a twist or impact",
          "Meniscus or cartilage tears",
          "Tendon inflammation around the knee",
          "Overuse from repetitive activity",
          "Injury following a fall",
        ],
      },
      {
        heading: "When to see an orthopaedic specialist",
        paragraphs: [
          "See a specialist if the knee is swollen, gives way, locks, or if pain limits walking and daily activities and does not settle within a few weeks.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is knee pain a normal part of ageing?",
        a: "Some wear with age is common, but significant pain is not something you simply have to accept. There are many treatment options that can help.",
      },
      {
        q: "Should I exercise with knee pain?",
        a: "Appropriate movement usually helps, but the right type and amount matters. A clinician or physiotherapist can guide activity suited to your knee.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis", "sports-injuries"],
  },
  {
    id: 17,
    slug: "knee-pain-when-should-you-see-an-orthopaedic-specialist",
    title: "Knee Pain: When Should You See an Orthopaedic Specialist?",
    category: "Knee Health",
    excerpt: "Many knee aches settle. These are the signs that mean it is time for a specialist opinion.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=An%20anatomical%20knee%20joint%20model%20beside%20a%20knee%20X-ray%20film%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-17&orientation=landscape&nocache=true",
    imageAlt: "Orthopaedic specialist examining a patient's knee",
    metaTitle: "Knee Pain: When to See a Specialist | Ortho Spine Care",
    metaDescription:
      "Learn the signs that mean knee pain needs an orthopaedic specialist, and what to expect at your consultation. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-15",
    updatedDate: "2026-06-16",
    readTime: "4 min read",
    tags: ["Knee Health", "Joint Health", "Patient Education"],
    content: [
      {
        heading: "Signs you should be assessed",
        bullets: [
          "Pain that lasts more than a few weeks",
          "Swelling that keeps returning",
          "The knee giving way or feeling unstable",
          "Locking or difficulty straightening the knee",
          "Pain that limits walking, stairs or sleep",
          "Inability to bear weight after an injury",
        ],
      },
      {
        heading: "What happens at the consultation",
        paragraphs: [
          "Your specialist will examine the knee, test its stability and movement, and review any imaging. The aim is to identify the cause before recommending treatment.",
        ],
      },
      {
        heading: "Treatment usually starts simply",
        paragraphs: [
          "Many knee problems improve with activity modification, physiotherapy and medication. Injections and surgery are considered when they offer a clear benefit for your situation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need an X-ray for knee pain?",
        a: "Not always. Imaging is arranged when it is likely to change the treatment plan, such as when arthritis or a structural injury is suspected.",
      },
      {
        q: "Can knee pain be treated without surgery?",
        a: "Yes. Most knee pain is managed without surgery. Surgery is reserved for specific problems such as significant ligament injury or advanced arthritis.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis", "sports-injuries"],
  },
  {
    id: 18,
    slug: "understanding-arthritis-and-joint-pain",
    title: "Understanding Arthritis and Joint Pain",
    category: "Joint Health",
    excerpt: "Arthritis is wear or inflammation of the joints. Understanding it helps you manage it well.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20hand%20and%20finger%20joints%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-18&orientation=landscape&nocache=true",
    imageAlt: "Person with joint stiffness due to arthritis",
    metaTitle: "Understanding Arthritis and Joint Pain | Ortho Spine Care",
    metaDescription:
      "Learn what arthritis is, why it causes joint pain and stiffness, and the treatment options that help. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-22",
    updatedDate: "2026-06-19",
    readTime: "6 min read",
    tags: ["Arthritis", "Joint Health", "Pain"],
    content: [
      {
        heading: "What arthritis means",
        paragraphs: [
          "Arthritis is the wear or inflammation of a joint. It can affect the knees, hips, hands and spine. It causes pain, stiffness and, over time, reduced movement.",
        ],
      },
      {
        heading: "Symptoms to expect",
        bullets: [
          "Joint pain that worsens with activity",
          "Morning stiffness that eases as you move",
          "Swelling and reduced range of movement",
          "A grating or creaking sensation",
        ],
      },
      {
        heading: "What helps",
        bullets: [
          "Staying active within comfort limits",
          "Maintaining a healthy body weight",
          "Physiotherapy to strengthen supporting muscles",
          "Pain relief medication during flare-ups",
          "Joint replacement in advanced cases where appropriate",
        ],
      },
    ],
    faqs: [
      {
        q: "Can arthritis be cured?",
        a: "Age-related arthritis cannot be reversed, but its symptoms can usually be managed well with a combination of activity, therapy and, where needed, medication or surgery.",
      },
      {
        q: "Should I stop moving a painful joint?",
        a: "Generally no. Appropriate movement helps maintain function. Rest is useful during a flare-up, but prolonged inactivity tends to worsen stiffness.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis"],
  },
  {
    id: 19,
    slug: "osteoarthritis-symptoms-and-treatment-options",
    title: "Osteoarthritis: Symptoms and Treatment Options",
    category: "Joint Health",
    excerpt: "Osteoarthritis is the most common form of joint wear. Here is how it is managed at each stage.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20a%20hip%20joint%20with%20early%20cartilage%20wear%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-19&orientation=landscape&nocache=true",
    imageAlt: "Older adult staying active while managing osteoarthritis",
    metaTitle: "Osteoarthritis: Symptoms and Treatment | Ortho Spine Care",
    metaDescription:
      "Understand the symptoms of osteoarthritis and the treatment options available, from lifestyle measures to joint replacement. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-29",
    updatedDate: "2026-06-21",
    readTime: "6 min read",
    tags: ["Arthritis", "Joint Health", "Treatment"],
    content: [
      {
        heading: "What osteoarthritis is",
        paragraphs: [
          "Osteoarthritis is the gradual wearing of the cartilage that cushions a joint. As the cartilage thins, the joint can become painful and stiff, most often in the knees, hips and hands.",
        ],
      },
      {
        heading: "Symptoms",
        bullets: [
          "Pain that increases with activity",
          "Stiffness, often worse in the morning or after rest",
          "Reduced movement and difficulty with stairs",
          "Swelling and, in some cases, a change in joint shape",
        ],
      },
      {
        heading: "Treatment at each stage",
        bullets: [
          "Early: activity modification, weight management and physiotherapy",
          "Moderate: medication and, in selected cases, injections",
          "Advanced: joint replacement when pain significantly limits daily life",
        ],
      },
    ],
    faqs: [
      {
        q: "Does cracking my knuckles cause osteoarthritis?",
        a: "No, there is no evidence that cracking joints causes osteoarthritis.",
      },
      {
        q: "Which joint replacement is most common?",
        a: "Knee and hip replacement are the most commonly performed joint replacement procedures for advanced arthritis.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis"],
  },
  {
    id: 20,
    slug: "knee-injury-common-causes-and-initial-care",
    title: "Knee Injury: Common Causes and Initial Care",
    category: "Knee Health",
    excerpt: "What to do in the first hours after a knee injury, and when it needs a specialist review.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Cold%20compress%20and%20bandage%20applied%20to%20a%20knee%20after%20injury%20on%20a%20clean%20bright%20surface%2C%20soft%20daylight%2C%20first%20aid%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-20&orientation=landscape&nocache=true",
    imageAlt: "Cold compress applied to an injured knee",
    metaTitle: "Knee Injury: Causes and Initial Care | Ortho Spine Care",
    metaDescription:
      "Learn the common causes of knee injuries and the correct initial care, plus the signs that need a specialist assessment. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-05",
    updatedDate: "2026-06-23",
    readTime: "5 min read",
    tags: ["Knee Health", "Sports Injuries", "First Aid"],
    content: [
      {
        heading: "Common causes of knee injuries",
        bullets: [
          "Twisting the knee during sport",
          "A direct impact or fall",
          "Landing awkwardly from a jump",
          "Overuse from repetitive activity",
        ],
      },
      {
        heading: "Initial care in the first 48 hours",
        bullets: [
          "Rest the knee and avoid painful activity",
          "Apply a cold pack for short periods",
          "Use a compression bandage for support",
          "Elevate the leg where possible",
        ],
      },
      {
        heading: "Signs that need a specialist review",
        bullets: [
          "Inability to bear weight",
          "Rapid, significant swelling",
          "The knee giving way or locking",
          "Persistent pain despite initial care",
        ],
      },
    ],
    faqs: [
      {
        q: "How long should I apply ice?",
        a: "Short applications of around 15 to 20 minutes, with a cloth between the ice and the skin, are usually recommended. Avoid applying ice directly to the skin.",
      },
      {
        q: "Do all knee injuries need a scan?",
        a: "No. Your clinician will decide whether imaging is needed based on the injury and examination findings.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 21,
    slug: "common-causes-of-shoulder-pain",
    title: "Common Causes of Shoulder Pain",
    category: "Shoulder Health",
    excerpt: "The shoulder has the greatest range of motion of any joint — and several structures that can cause pain.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20shoulder%20joint%20and%20rotator%20cuff%20tendons%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-21&orientation=landscape&nocache=true",
    imageAlt: "Person holding the shoulder due to shoulder pain",
    metaTitle: "Common Causes of Shoulder Pain | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to the common causes of shoulder pain, including rotator cuff problems and tendon injuries, from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-12",
    updatedDate: "2026-06-24",
    readTime: "5 min read",
    tags: ["Shoulder Health", "Joint Health", "Tendon"],
    content: [
      {
        heading: "Why the shoulder is different",
        paragraphs: [
          "The shoulder is a shallow ball-and-socket joint that relies on muscles and tendons for stability. That design gives it huge movement, but also makes it vulnerable to tendon and soft-tissue problems.",
        ],
      },
      {
        heading: "Common causes",
        bullets: [
          "Rotator cuff tendon injuries",
          "Tendon inflammation from repetitive overhead activity",
          "Shoulder joint wear or arthritis",
          "Frozen shoulder, with progressive stiffness",
          "Injury or dislocation",
        ],
      },
      {
        heading: "When to seek help",
        paragraphs: [
          "See a specialist if pain lasts more than a few weeks, limits reaching or lifting, or is accompanied by weakness or a feeling that the shoulder may give way.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can shoulder pain come from the neck?",
        a: "Yes. Nerve problems in the neck can cause pain felt in the shoulder and arm. A specialist assessment helps tell the difference.",
      },
      {
        q: "Is shoulder pain common after 50?",
        a: "Tendon problems and joint wear become more common with age, but pain is not inevitable and can often be treated effectively.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 22,
    slug: "shoulder-pain-symptoms-you-should-not-ignore",
    title: "Shoulder Pain: Symptoms You Should Not Ignore",
    category: "Shoulder Health",
    excerpt: "Most shoulder pain is treatable. These symptoms mean it is time to get it looked at.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=An%20anatomical%20shoulder%20joint%20model%20beside%20a%20shoulder%20scan%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-22&orientation=landscape&nocache=true",
    imageAlt: "Doctor assessing shoulder movement during an examination",
    metaTitle: "Shoulder Pain Symptoms Not to Ignore | Ortho Spine Care",
    metaDescription:
      "Learn which shoulder pain symptoms should not be ignored, and when to seek specialist care. Practical guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-19",
    updatedDate: "2026-06-26",
    readTime: "4 min read",
    tags: ["Shoulder Health", "Tendon", "Patient Education"],
    content: [
      {
        heading: "Symptoms worth assessing",
        bullets: [
          "Pain that prevents you raising the arm",
          "Weakness when lifting or reaching",
          "A catching or clicking sensation with movement",
          "Night pain that disturbs sleep",
          "Pain that follows an injury or fall",
          "Sudden loss of movement with no clear cause",
        ],
      },
      {
        heading: "What assessment involves",
        paragraphs: [
          "Your specialist examines the shoulder movement and strength and may arrange imaging to look at the tendons and joint. This identifies the cause and guides treatment.",
        ],
      },
      {
        heading: "Treatment is often non-surgical",
        paragraphs: [
          "Many shoulder problems improve with rest, physiotherapy and medication. Surgery, such as tendon repair, is considered when the injury makes it necessary.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a torn shoulder tendon heal on its own?",
        a: "Some partial tears improve with non-surgical treatment. Complete tears may need surgical repair, especially in active people. A specialist can advise.",
      },
      {
        q: "Is night shoulder pain a warning sign?",
        a: "Night pain is common in several shoulder conditions and is worth assessing, as it can indicate tendon or joint inflammation.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 23,
    slug: "understanding-joint-stiffness",
    title: "Understanding Joint Stiffness",
    category: "Joint Health",
    excerpt: "Stiffness is one of the most common joint complaints — and its pattern tells you a lot.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20wrist%20and%20finger%20joints%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-23&orientation=landscape&nocache=true",
    imageAlt: "Person easing joint stiffness in the hands",
    metaTitle: "Understanding Joint Stiffness | Ortho Spine Care",
    metaDescription:
      "Learn what causes joint stiffness, how its pattern helps diagnosis, and the simple steps and treatments that relieve it. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-26",
    updatedDate: "2026-06-27",
    readTime: "5 min read",
    tags: ["Joint Health", "Arthritis", "Stiffness"],
    content: [
      {
        heading: "What stiffness means",
        paragraphs: [
          "Stiffness is a reduced ease of movement in a joint. It can follow rest, injury or the slow changes of arthritis, and the timing of the stiffness is an important clue.",
        ],
      },
      {
        heading: "Why the pattern matters",
        bullets: [
          "Brief morning stiffness that eases with movement often suggests age-related wear",
          "Stiffness lasting a long time each morning may suggest inflammation",
          "Stiffness after a period of rest can point to osteoarthritis",
          "Stiffness after an injury may indicate damage inside the joint",
        ],
      },
      {
        heading: "What helps",
        bullets: [
          "Gentle movement and stretching",
          "Warm showers or heat before activity",
          "Physiotherapy to maintain range of motion",
          "Treatment of the underlying condition where needed",
        ],
      },
    ],
    faqs: [
      {
        q: "Is stiffness always a sign of arthritis?",
        a: "No. Stiffness can also follow injury, inactivity or tendon problems. A clinician can help identify the cause.",
      },
      {
        q: "Should I stretch a stiff joint?",
        a: "Gentle movement usually helps, but avoid forcing a joint into pain. A physiotherapist can guide appropriate exercises.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis"],
  },
  {
    id: 24,
    slug: "how-to-protect-your-joints-as-you-age",
    title: "How to Protect Your Joints as You Age",
    category: "Joint Health",
    excerpt: "You cannot stop ageing, but you can do a great deal to keep your joints working comfortably.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20healthy%20knee%20and%20hip%20joints%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-24&orientation=landscape&nocache=true",
    imageAlt: "Active older couple walking to protect their joints",
    metaTitle: "How to Protect Your Joints as You Age | Ortho Spine Care",
    metaDescription:
      "Practical ways to protect your joints as you get older, from staying active to managing weight. Patient-friendly advice from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-05",
    updatedDate: "2026-06-29",
    readTime: "5 min read",
    tags: ["Joint Health", "Ageing", "Prevention"],
    content: [
      {
        heading: "Movement keeps joints healthy",
        paragraphs: [
          "Joints rely on movement for nourishment and stability. Regular, moderate activity keeps the muscles around them strong and the joints mobile.",
        ],
      },
      {
        heading: "Ways to protect your joints",
        bullets: [
          "Choose low-impact activity such as walking, cycling or swimming",
          "Maintain a healthy body weight to reduce load on the knees and hips",
          "Warm up before activity and build up gradually",
          "Use good technique when lifting and carrying",
          "Include strength work for the muscles that support the joints",
        ],
      },
      {
        heading: "Listening to your body",
        paragraphs: [
          "Muscle soreness after activity is normal; sharp joint pain is not. If pain persists or a joint swells, it is worth having it assessed rather than pushing through.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is running bad for the knees?",
        a: "Recreational running is not inherently harmful. Good technique, sensible training and appropriate recovery matter more than the activity itself.",
      },
      {
        q: "Do supplements protect joints?",
        a: "Evidence for joint supplements is mixed. Discuss any supplement with your doctor rather than relying on it in place of proven measures.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis"],
  },
  {
    id: 25,
    slug: "exercises-and-lifestyle-habits-for-joint-health",
    title: "Exercises and Lifestyle Habits for Joint Health",
    category: "Joint Health",
    excerpt: "The right kind of exercise supports joints rather than wearing them out. Here is how to think about it.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Neatly%20arranged%20resistance%20bands%2C%20a%20foam%20roller%20and%20an%20exercise%20mat%20on%20a%20clean%20light%20floor%20in%20a%20bright%20studio%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-25&orientation=landscape&nocache=true",
    imageAlt: "Gentle strengthening exercises for joint health",
    metaTitle: "Exercises and Habits for Joint Health | Ortho Spine Care",
    metaDescription:
      "Learn how the right exercise and lifestyle habits support long-term joint health. Practical patient guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-12",
    updatedDate: "2026-06-30",
    readTime: "5 min read",
    tags: ["Joint Health", "Exercise", "Wellness"],
    content: [
      {
        heading: "Three kinds of exercise that help joints",
        bullets: [
          "Range-of-motion exercise to keep joints mobile",
          "Strengthening exercise to support the joint",
          "Low-impact aerobic activity to keep you fit without heavy load",
        ],
      },
      {
        heading: "Lifestyle habits that support joints",
        bullets: [
          "Stay at a healthy weight",
          "Eat a balanced diet with adequate calcium and vitamin D",
          "Avoid smoking and limit excess alcohol",
          "Get enough sleep, which supports recovery",
          "Build activity up gradually rather than in bursts",
        ],
      },
      {
        heading: "Getting advice that fits you",
        paragraphs: [
          "If you already have joint pain, a physiotherapist or specialist can recommend exercises suited to your joints. The right programme makes movement easier rather than harder.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does exercise wear out joints faster?",
        a: "Appropriate exercise supports joint health. Problems usually come from sudden spikes in activity or poor technique rather than moderate, consistent movement.",
      },
      {
        q: "How much exercise do I need?",
        a: "Aim for regular moderate activity most days, alongside strengthening work. Build up gradually and adjust to your comfort.",
      },
    ],
    relatedServices: ["joint-disorders"],
    relatedConditions: ["arthritis"],
  },
];